/**
 * Cookie-based Cache Manager
 * @description 쿠키를 사용한 클라이언트 캐시 관리
 */

export interface CacheConfig {
  key: string;
  ttl: number; // milliseconds
}

const DEFAULT_CONFIG: CacheConfig = {
  key: 'nde_categories_cache',
  ttl: 10 * 60 * 1000, // 10분
};

/**
 * 쿠키 캐시 매니저
 */
export const createCacheManager = <T>(config: Partial<CacheConfig> = {}) => {
  const { key, ttl } = { ...DEFAULT_CONFIG, ...config };

  /**
   * 캐시 데이터 조회
   */
  const get = (): T | null => {
    try {
      const match = document.cookie.match(new RegExp(`(^| )${key}=([^;]+)`));
      if (!match) {
        return null;
      }

      return JSON.parse(decodeURIComponent(match[2])) as T;
    } catch {
      clear();
      return null;
    }
  };

  /**
   * 캐시 데이터 저장
   */
  const set = (data: T): void => {
    try {
      const expires = new Date(Date.now() + ttl).toUTCString();
      const encoded = encodeURIComponent(JSON.stringify(data));
      // biome-ignore lint/suspicious/noDocumentCookie: 고도몰 환경에서 Cookie Store API 미지원
      document.cookie = `${key}=${encoded}; expires=${expires}; path=/`;
    } catch {
      // 저장 실패 무시
    }
  };

  /**
   * 캐시 데이터 삭제
   */
  const clear = (): void => {
    // biome-ignore lint/suspicious/noDocumentCookie: 고도몰 환경에서 Cookie Store API 미지원
    document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
  };

  return { get, set, clear };
};

/**
 * 카테고리 전용 캐시 매니저 (싱글톤)
 */
let categoryCacheInstance: ReturnType<typeof createCacheManager<unknown[]>> | null = null;

export const getCategoryCache = <T>() => {
  if (!categoryCacheInstance) {
    categoryCacheInstance = createCacheManager<T[]>();
  }
  return categoryCacheInstance as ReturnType<typeof createCacheManager<T[]>>;
};
